import { useEffect, useCallback, useState } from 'react';

export interface SettingsData {
  [key: string]: any;
}

export interface TimerData {
  status: 'running' | 'stopped';
  timestamp: number;
  project: string;
  [key: string]: any;
}

interface UseSettingsRefreshProps {
  onSettingsRefresh?: (settings: SettingsData) => void;
  onTimerRefresh?: (timer: TimerData) => void;
}

export const useSettingsRefresh = ({ 
  onSettingsRefresh, 
  onTimerRefresh 
}: UseSettingsRefreshProps = {}) => {
  
  const handleSettingsRefresh = useCallback((event: CustomEvent) => {
    console.log('🔄 Settings refresh event received in hook:', event.detail);
    if (onSettingsRefresh) {
      onSettingsRefresh(event.detail);
    }
  }, [onSettingsRefresh]);

  const handleTimerRefresh = useCallback((event: CustomEvent) => {
    console.log('⏱️ Timer refresh event received in hook:', event.detail);
    if (onTimerRefresh) {
      onTimerRefresh(event.detail);
    }
  }, [onTimerRefresh]);

  // Handle legacy settings refresh events
  const handleLegacySettingsRefresh = useCallback((event: CustomEvent) => {
    console.log('🔄 Legacy settings refresh event received in hook:', event.detail);
    if (onSettingsRefresh) {
      // Convert legacy event format to new format
      onSettingsRefresh({
        message: event.detail.message,
        timestamp: event.detail.timestamp,
        tenant_id: event.detail.tenantId
      });
    }
  }, [onSettingsRefresh]);

  useEffect(() => {
    // Listen to new settings refresh events
    if (onSettingsRefresh) {
      window.addEventListener('settings_refreshed', handleSettingsRefresh as EventListener);
      // Also listen to legacy events for backwards compatibility
      window.addEventListener('onespace-settings-refresh', handleLegacySettingsRefresh as EventListener);
    }

    // Listen to timer refresh events
    if (onTimerRefresh) {
      window.addEventListener('timer_refreshed', handleTimerRefresh as EventListener);
    }

    return () => {
      if (onSettingsRefresh) {
        window.removeEventListener('settings_refreshed', handleSettingsRefresh as EventListener);
        window.removeEventListener('onespace-settings-refresh', handleLegacySettingsRefresh as EventListener);
      }
      if (onTimerRefresh) {
        window.removeEventListener('timer_refreshed', handleTimerRefresh as EventListener);
      }
    };
  }, [handleSettingsRefresh, handleLegacySettingsRefresh, handleTimerRefresh, onSettingsRefresh, onTimerRefresh]);

  return {
    // You can add additional functionality here if needed
  };
};

// Hook for easy timer state management
export const useTimerState = () => {
  const [timerData, setTimerData] = useState<TimerData | null>(null);

  useSettingsRefresh({
    onTimerRefresh: (timer: TimerData) => {
      setTimerData(timer);
    }
  });

  // Format time for display
  const formatTime = useCallback((timestamp: number) => {
    const now = Date.now();
    const elapsed = Math.floor((now - timestamp) / 1000);
    
    const hours = Math.floor(elapsed / 3600);
    const minutes = Math.floor((elapsed % 3600) / 60);
    const seconds = elapsed % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  return {
    timerData,
    formatTime,
    isRunning: timerData?.status === 'running',
    project: timerData?.project || 'Unknown Project'
  };
};

export default useSettingsRefresh; 